import { Component, ElementRef, OnDestroy, OnInit, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { BuildingModalService, BuildingData } from './building-modal.service';

interface FloorData {
  floorName: string;
  floorArea: number,
  floorUom: string,
  tenants: BuildingData[];
  totalUsage: number;
}

@Component({
  selector: 'app-building-modal',
  templateUrl: './building-modal.component.html',
  styleUrls: ['./building-modal.component.css']
})
export class BuildingModalComponent implements OnInit, OnDestroy {

  isVisible = false;
  title = 'Building Details';
  buildingData: BuildingData[] = [];
  groupedBuildingData: FloorData[] = [];
  buildingName?: string;
  modalHeight: string = 'auto';

  private modalSubscription: Subscription | undefined;

  constructor(
    private modalService: BuildingModalService,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.modalSubscription = this.modalService.modalState$.subscribe(state => {
      if (state) {
        this.isVisible = state.isOpen;
        this.buildingData = state.data || [];
        this.buildingName = state.buildingName;
        
        this.groupedBuildingData = this.groupDataByFloor(this.buildingData);
        this.updateTitle();

        if (this.isVisible) {
          document.body.classList.add('modal-open');
          // Tunggu hingga DOM di-render kemudian hitung tinggi
          setTimeout(() => this.calculateModalHeight(), 100);
        }
      } else {
        this.isVisible = false;
        document.body.classList.remove('modal-open');
      }
    });
  }

  // Method untuk menghitung tinggi modal secara dinamis
  private calculateModalHeight(): void {
    if (!this.isVisible) return;

    const modalDialog = this.elementRef.nativeElement.querySelector('.modal-dialog');
    const modalContent = this.elementRef.nativeElement.querySelector('.modal-content');
    
    if (!modalDialog || !modalContent) return;

    // Reset height untuk mendapatkan tinggi natural
    modalDialog.style.maxHeight = 'none';
    modalContent.style.maxHeight = 'none';
    modalDialog.style.height = 'auto';
    modalContent.style.height = 'auto';

    // Hitung tinggi total konten
    const modalHeader = this.elementRef.nativeElement.querySelector('.modal-header');
    const legendFooter = this.elementRef.nativeElement.querySelector('.legend-footer');
    const tableContainer = this.elementRef.nativeElement.querySelector('.table-container');
    
    const headerHeight = modalHeader?.offsetHeight || 0;
    const legendHeight = legendFooter?.offsetHeight || 0;
    const tableHeight = tableContainer?.offsetHeight || 0;

    // Total tinggi konten
    const contentHeight = headerHeight + legendHeight + tableHeight;
    
    // Dapatkan tinggi viewport
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Margin atas+bawah (2rem = 32px)
    const margin = 32;

    // Target tinggi modal = konten + margin
    let targetHeight = contentHeight + margin;

    // Untuk mobile, batasi maksimal 90vh
    if (viewportWidth <= 768) {
      const maxMobileHeight = viewportHeight * 0.9;
      if (targetHeight > maxMobileHeight) {
        targetHeight = maxMobileHeight;
      }
    } else {
      // Untuk desktop, batasi maksimal 85vh jika konten sangat banyak
      const maxDesktopHeight = viewportHeight * 0.85;
      if (targetHeight > maxDesktopHeight) {
        targetHeight = maxDesktopHeight;
      }
    }

    // Pastikan tinggi minimal untuk estetika
    const minHeight = 300;
    if (targetHeight < minHeight) {
      targetHeight = minHeight;
    }

    console.log('Calculated modal height:', {
      headerHeight,
      legendHeight,
      tableHeight,
      contentHeight,
      viewportHeight,
      targetHeight
    });

    // Terapkan tinggi
    this.modalHeight = targetHeight + 'px';
  }

  // Recalculate height on window resize
  @HostListener('window:resize')
  onWindowResize() {
    if (this.isVisible) {
      setTimeout(() => this.calculateModalHeight(), 150);
    }
  }

  // Call calculateHeight when data changes
  ngAfterViewChecked() {
    if (this.isVisible && this.groupedBuildingData.length > 0) {
      // Gunakan debounce untuk menghindari multiple calls
      clearTimeout((this as any)._heightTimeout);
      (this as any)._heightTimeout = setTimeout(() => {
        this.calculateModalHeight();
      }, 50);
    }
  }

  private updateTitle(): void {
    if (this.buildingName) {
      this.title = `${this.buildingName} - Floor Details`;
    } else {
      this.title = 'Building Details';
    }
  }

  private groupDataByFloor(data: BuildingData[]): FloorData[] {
    const grouped: { [key: string]: FloorData } = {};
    
    data.forEach(item => {
      if (!grouped[item.floor_name]) {
        grouped[item.floor_name] = {
          floorName: item.floor_name,
          floorArea: item.floor_area,
          floorUom:  item.uom,
          tenants: [],
          totalUsage: 0
        };
      }
      grouped[item.floor_name].tenants.push(item);
      grouped[item.floor_name].totalUsage += item.pctg_usage;
    });
    
    return Object.values(grouped).sort((a, b) => {
      const numA = parseInt(a.floorName);
      const numB = parseInt(b.floorName);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numB - numA;
      }
      return a.floorName.localeCompare(b.floorName);
    });
  }

  getSegmentClass(tenantType: string): string {
    if (!tenantType) return 'segment-default';

    const type = tenantType.toLowerCase().trim();

    if (type.includes('vacant') || type.includes('available')) {
      return 'segment-vacant';
    }

    if (type.includes('non group')) {
      return 'segment-non-group';
    }
    
    if (type.includes('group-unpaid')) {
      return 'segment-group-unpaid';
    }

    if (type.includes('group-paid')) {
      return 'segment-group-paid';
    }

    if (type.includes('reserved')) {
      return 'segment-reserved';
    }
    

    return 'segment-vacant';
  }

  getSegmentTooltip(tenant: BuildingData): string {
    return `${tenant.tenant_name}\n${tenant.used_area || 0} m² • ${tenant.pctg_usage}%`;
  }

  ngOnDestroy(): void {
    if (this.modalSubscription) {
      this.modalSubscription.unsubscribe();
    }
    document.body.classList.remove('modal-open');
    clearTimeout((this as any)._heightTimeout);
  }

  close(): void {
    this.modalService.closeModal();
  }

  hasSmallSegments(tenants: BuildingData[]): boolean {
    return tenants.some(tenant => tenant.pctg_usage < 5);
  }

  getTextColor(tenantType: string): string {
    const type = tenantType?.toLowerCase() || '';
    
    if (type.includes('vacant') || type.includes('available')) {
      return 'text-dark';
    } else if (type.includes('group') && type.includes('paid')) {
      return 'text-dark';
    } else if (type.includes('group') && (type.includes('non') || type.includes('unpaid'))) {
      return 'text-white';
    } else if (type.includes('non group')) {
      return 'text-white';
    } else if (type.includes('reseerved')) {
      return 'text-white';
    } else {
      return 'text-white';
    }
  }

  getShortTenantName(tenantName: string, maxLength: number = 20): string {
    if (tenantName.length > maxLength) {
      return tenantName.substring(0, maxLength - 3) + '...';
    }
    return tenantName;
  }
}